import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Contact Us | The Cornwall SEO Co.</title>
        <meta name="description" content="If you would like to know more about how we can help you reach your internet marketing goals, please use the form below." />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-2xl"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <h1 className="text-6xl md:text-7xl font-heading font-black text-primary mb-6 uppercase italic tracking-tighter">
              Get in <br/> Touch
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Contact Section with Map */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 mb-20">
            {/* Contact Information */}
            <motion.div
              className="space-y-12"
              initial="hidden"
              whileInView="visible"
              variants={containerVariants}
              viewport={{ once: true }}
            >
              <div>
                <h2 className="text-4xl font-heading font-black text-primary mb-8 uppercase italic">Contact Info</h2>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 mt-1">
                      <Phone size={24} className="text-black" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm uppercase font-semibold tracking-wider mb-2">Phone</p>
                      <a href="tel:0172674161" className="text-white font-black text-lg hover:text-primary transition-colors">
                        01726 74161
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 mt-1">
                      <Mail size={24} className="text-black" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm uppercase font-semibold tracking-wider mb-2">Email</p>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black text-lg hover:text-primary transition-colors">
                        hello@thecornwallseoco.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 mt-1">
                      <MessageCircle size={24} className="text-black" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm uppercase font-semibold tracking-wider mb-2">WhatsApp</p>
                      <a href="https://wa.me/447792646061" target="_blank" rel="noopener noreferrer" className="text-white font-black text-lg hover:text-primary transition-colors">
                        +44 7792 646061
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0 mt-1">
                      <MapPin size={24} className="text-black" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm uppercase font-semibold tracking-wider mb-2">Address</p>
                      <p className="text-white font-black text-lg">
                        87 Alexandra Road<br/>
                        St Austell<br/>
                        Cornwall PL25 4QW<br/>
                        United Kingdom
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 p-8 rounded-none">
                <h3 className="text-xl font-heading font-black text-primary mb-4 uppercase italic">Office Hours</h3>
                <div className="space-y-2 text-white/80 font-medium">
                  <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                  <p>
                    Out of Hours:{" "}
                    <a 
                      href="https://wa.me/447792646061" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-white transition-colors font-black"
                    >
                      WhatsApp
                    </a>
                  </p>
                  <p className="text-sm mt-4">We aim to respond to all enquiries within 24 hours.</p>
                </div>
              </div>
            </motion.div>

            {/* Google Map */}
            <motion.div
              className="h-full min-h-96"
              initial="hidden"
              whileInView="visible"
              variants={containerVariants}
              viewport={{ once: true }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2492.3485469215427!2d-4.7842436!3d50.338414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x486b6eceaae46795%3A0x81f8227eefc56228!2sThe%20Cornwall%20SEO%20Co!5e0!3m2!1sen!2suk!4v1710600000000"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-none"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-primary">
        <div className="container mx-auto px-6">
          <div className="bg-black p-10 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)]">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={containerVariants}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-heading font-black text-primary mb-6 uppercase italic tracking-tighter">Send us a Message</h2>
              <p className="text-white/80 text-xl mb-12 font-medium">
                Fill out the form below and our team will get back to you as soon as possible.
              </p>

              <form action="/contact.php" method="POST" className="grid md:grid-cols-2 gap-6 max-w-4xl">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-14 font-black placeholder:text-black/30 md:col-span-1" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-14 font-black placeholder:text-black/30 md:col-span-1" />
                <Input name="phone" placeholder="PHONE" className="bg-white border-none rounded-none h-14 font-black placeholder:text-black/30 md:col-span-1" />
                <Input name="company" placeholder="COMPANY" className="bg-white border-none rounded-none h-14 font-black placeholder:text-black/30 md:col-span-1" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-14 font-black placeholder:text-black/30 md:col-span-2 hidden" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-32 font-black placeholder:text-black/30 md:col-span-2" />
                <Button type="submit" className="w-full md:col-span-2 bg-primary text-black hover:bg-white h-16 font-black uppercase italic text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
